// §7.2 — the OPFS working store.
//
// `navigator.storage.getDirectory()` mirrors the §7.1 tree 1:1: a VFS path is the OPFS path.
// Four things in here are spec, not taste:
//
//  1. **Directory handles are cached in an LRU of 256.** Walking `/Users/guest/Documents` costs
//     three async `getDirectoryHandle` calls otherwise, on every single operation.
//  2. **Every write is temp → flush → rename**, so a crash never leaves a truncated file. The
//     rename is the commit point, and it is the only step that is not abortable.
//  3. **Writes go through `createSyncAccessHandle` in a worker where available**, falling back
//     to `createWritable` — and, where `move()` is missing too (Firefox, Safari — D-042), to a
//     direct `createWritable` on the destination.
//  4. **`navigator.storage.persist()` is requested on the first write of a session.**
//
// This module is the byte layer and nothing more. It knows no permissions: `confine()` (§7.1)
// runs above it, and the §7.1 *layout* is `tree.ts`'s job.
import {
  basename,
  isUnder,
  joinVfs,
  normalizeVfsPath,
  parentOf,
  type PathError,
  type VfsPath,
} from '@hangar/shared';
import { nanoid } from 'nanoid';
import {
  TEMP_DIR,
  hasRename,
  type MovableFileHandle,
  type WorkerMessage,
  type WorkerRequest,
} from './opfs-protocol';

/** §7.2 — the directory-handle LRU. */
export const HANDLE_CACHE_LIMIT = 256;

/** Temp debris older than this is another session's crash residue, and is swept at open. */
export const TEMP_SWEEP_AGE_MS = 5 * 60_000;

/** A worker that has not announced itself by now is treated as absent. */
const WORKER_READY_TIMEOUT_MS = 2_000;

export type OpfsErrorCode =
  | PathError
  | 'NOT_FOUND'
  | 'NOT_A_DIRECTORY'
  | 'IS_A_DIRECTORY'
  | 'NOT_EMPTY'
  | 'ABORTED'
  | 'QUOTA'
  | 'REFUSED'
  | 'UNSUPPORTED';

export class OpfsError extends Error {
  readonly code: OpfsErrorCode;
  readonly path: string;

  constructor(code: OpfsErrorCode, path: string, message?: string) {
    super(message ?? `opfs: ${code} for ${JSON.stringify(path)}`);
    this.name = 'OpfsError';
    this.code = code;
    this.path = path;
  }
}

export interface OpfsStat {
  path: VfsPath;
  name: string;
  isDir: boolean;
  size: number;
  /** Epoch milliseconds. OPFS keeps no directory mtime, so directories report 0. */
  mtime: number;
}

export interface WriteOptions {
  /**
   * Aborts the write. Checked on entry and again once the temp bytes have landed — i.e. right
   * before the rename. An aborted write never touches the destination.
   */
  signal?: AbortSignal;
}

export type WriteMode = 'sync-worker' | 'writable' | 'unknown';

export interface OpfsDiagnostics {
  writeMode: WriteMode;
  /** False on Firefox and Safari today; writes then commit through `createWritable` (D-042). */
  renameSupported: boolean;
  cachedHandles: number;
  /** Temp files awaiting a rename. Steady state is zero. */
  tempEntries: number;
}

export interface OpfsStore {
  read(path: string): Promise<Uint8Array>;
  write(path: string, data: Uint8Array | string, options?: WriteOptions): Promise<OpfsStat>;
  list(dir: string): Promise<OpfsStat[]>;
  mkdir(path: string): Promise<OpfsStat>;
  move(from: string, to: string): Promise<OpfsStat>;
  remove(path: string, options?: { recursive?: boolean }): Promise<void>;
  /** `null` when nothing is there — the one call that does not throw `NOT_FOUND`. */
  stat(path: string): Promise<OpfsStat | null>;
  exists(path: string): Promise<boolean>;
  estimate(): Promise<{ usage: number; quota: number }>;
  diagnostics(): Promise<OpfsDiagnostics>;
  /**
   * §18 F16 — drop the cached handles at and under `path`. Another tab writing to this origin
   * makes ours stale, and a stale directory handle answers about a directory that is gone.
   */
  invalidate(path: string): void;
  dispose(): void;
}

export interface OpfsStoreOptions {
  /** Set false to force the `createWritable` path — the fallback needs proving too. */
  useWorker?: boolean;
}

// ── the handle LRU ─────────────────────────────────────────────────────────────────────────

export interface HandleCache<T> {
  get(key: string): T | undefined;
  set(key: string, value: T): void;
  /** Drop `path` and everything beneath it — what a move or a delete invalidates. */
  invalidate(path: VfsPath): void;
  clear(): void;
  keys(): string[];
  readonly size: number;
}

export function createHandleCache<T>(limit = HANDLE_CACHE_LIMIT): HandleCache<T> {
  // A Map iterates in insertion order, so re-inserting on a hit is the whole LRU.
  const entries = new Map<string, T>();

  return {
    get(key) {
      const value = entries.get(key);
      if (value === undefined) return undefined;
      entries.delete(key);
      entries.set(key, value);
      return value;
    },
    set(key, value) {
      entries.delete(key);
      entries.set(key, value);
      while (entries.size > limit) {
        const oldest = entries.keys().next();
        if (oldest.done === true) break;
        entries.delete(oldest.value);
      }
    },
    invalidate(path) {
      for (const key of [...entries.keys()]) {
        if (isUnder(key, path)) entries.delete(key);
      }
    },
    clear() {
      entries.clear();
    },
    keys: () => [...entries.keys()],
    get size() {
      return entries.size;
    },
  };
}

// ── error mapping ──────────────────────────────────────────────────────────────────────────

function domName(error: unknown): string {
  return error instanceof DOMException ? error.name : '';
}

/** Turn a DOM exception into an `OpfsError`, leaving an `OpfsError` alone. */
function wrap(error: unknown, path: string, fallback: OpfsErrorCode = 'REFUSED'): OpfsError {
  if (error instanceof OpfsError) return error;
  const name = domName(error);
  if (name === 'NotFoundError') return new OpfsError('NOT_FOUND', path);
  if (name === 'InvalidModificationError') return new OpfsError('NOT_EMPTY', path);
  if (name === 'QuotaExceededError') return new OpfsError('QUOTA', path);
  if (name === 'AbortError') return new OpfsError('ABORTED', path);
  const message = error instanceof Error ? error.message : String(error);
  return new OpfsError(fallback, path, `opfs: ${fallback} for ${JSON.stringify(path)} — ${message}`);
}

const encoder = new TextEncoder();

/**
 * Bytes backed by a plain `ArrayBuffer`. Both `createWritable` and a `postMessage` transfer
 * refuse a `SharedArrayBuffer` view, so the distinction is worth carrying in the types.
 */
type Bytes = Uint8Array<ArrayBuffer>;

function toBytes(data: Uint8Array | string): Bytes {
  if (typeof data === 'string') return encoder.encode(data);
  const buffer = data.buffer;
  return buffer instanceof ArrayBuffer
    ? new Uint8Array(buffer, data.byteOffset, data.byteLength)
    : new Uint8Array(data);
}

function requirePath(input: string): VfsPath {
  const parsed = normalizeVfsPath(input);
  if (!parsed.ok) throw new OpfsError(parsed.error, input);
  return parsed.path;
}

function directoryStat(path: VfsPath): OpfsStat {
  return { path, name: basename(path), isDir: true, size: 0, mtime: 0 };
}

// ── the store ──────────────────────────────────────────────────────────────────────────────

export function createOpfsStore(options: OpfsStoreOptions = {}): OpfsStore {
  const cache = createHandleCache<FileSystemDirectoryHandle>(HANDLE_CACHE_LIMIT);
  const session = nanoid(8);
  const renameSupported = hasRename();

  let rootPromise: Promise<FileSystemDirectoryHandle> | null = null;
  let persistRequested = false;
  let tempCounter = 0;

  let worker: Worker | null = null;
  let workerReady: Promise<boolean> | null = null;
  let workerAlive = false;
  let requestCounter = 0;
  const pending = new Map<number, (ok: boolean) => void>();

  // ── roots and navigation ─────────────────────────────────────────────────────────────────

  async function root(): Promise<FileSystemDirectoryHandle> {
    rootPromise ??= openRoot();
    return rootPromise;
  }

  async function openRoot(): Promise<FileSystemDirectoryHandle> {
    const storage: StorageManager | undefined = globalThis.navigator?.storage;
    if (typeof storage?.getDirectory !== 'function') {
      throw new OpfsError('UNSUPPORTED', '/', 'opfs: this browser has no origin private file system');
    }
    const handle = await storage.getDirectory();
    await sweepTemp(handle);
    return handle;
  }

  /**
   * Remove temp debris a previous session left behind. Only entries older than
   * `TEMP_SWEEP_AGE_MS` are touched, so a second tab writing right now is not robbed of its
   * temp file (multi-tab safety proper is M6.7).
   */
  async function sweepTemp(handle: FileSystemDirectoryHandle): Promise<void> {
    let temps: FileSystemDirectoryHandle;
    try {
      temps = await handle.getDirectoryHandle(TEMP_DIR, { create: false });
    } catch {
      return; // nothing has ever been written on this origin
    }
    const cutoff = Date.now() - TEMP_SWEEP_AGE_MS;
    const stale: string[] = [];
    for await (const [name, child] of temps.entries()) {
      if (child.kind !== 'file') {
        stale.push(name);
        continue;
      }
      const file = await (child as FileSystemFileHandle).getFile();
      if (file.lastModified < cutoff) stale.push(name);
    }
    for (const name of stale) {
      try {
        await temps.removeEntry(name, { recursive: true });
      } catch {
        // Another tab may own it. Debris is harmless; failing to open the store is not.
      }
    }
  }

  async function tempDirectory(): Promise<FileSystemDirectoryHandle> {
    return (await root()).getDirectoryHandle(TEMP_DIR, { create: true });
  }

  async function directory(path: VfsPath, create: boolean): Promise<FileSystemDirectoryHandle> {
    const base = await root();
    if (path === '/') return base;

    const cached = cache.get(path);
    if (cached) return cached;

    const parsed = normalizeVfsPath(path);
    if (!parsed.ok) throw new OpfsError(parsed.error, path);

    let handle = base;
    let walked = '';
    for (const segment of parsed.segments) {
      walked = `${walked}/${segment}`;
      const hit = cache.get(walked);
      if (hit) {
        handle = hit;
        continue;
      }
      try {
        handle = await handle.getDirectoryHandle(segment, { create });
      } catch (error) {
        if (domName(error) === 'TypeMismatchError') {
          throw new OpfsError('NOT_A_DIRECTORY', walked);
        }
        throw wrap(error, walked);
      }
      cache.set(walked, handle);
    }
    return handle;
  }

  async function fileHandle(path: VfsPath, create: boolean): Promise<FileSystemFileHandle> {
    const parent = await directory(parentOf(path), create);
    try {
      return await parent.getFileHandle(basename(path), { create });
    } catch (error) {
      if (domName(error) === 'TypeMismatchError') throw new OpfsError('IS_A_DIRECTORY', path);
      throw wrap(error, path);
    }
  }

  // ── the write worker ─────────────────────────────────────────────────────────────────────

  function ensureWorker(): Promise<boolean> {
    workerReady ??= startWorker();
    return workerReady;
  }

  function startWorker(): Promise<boolean> {
    if (options.useWorker === false || typeof Worker === 'undefined') return Promise.resolve(false);

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const settle = (ok: boolean): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        workerAlive = ok;
        if (!ok) spawned?.terminate();
        resolve(ok);
      };
      const timer = setTimeout(() => {
        settle(false);
      }, WORKER_READY_TIMEOUT_MS);

      let spawned: Worker | null = null;
      try {
        spawned = new Worker(new URL('./opfs-worker.ts', import.meta.url), { type: 'module' });
      } catch {
        settle(false);
        return;
      }
      worker = spawned;

      spawned.addEventListener('message', (event: MessageEvent<WorkerMessage>) => {
        const message = event.data;
        if (message.kind === 'ready') {
          settle(message.syncAccess);
          return;
        }
        const resolvePending = pending.get(message.id);
        if (!resolvePending) return;
        pending.delete(message.id);
        resolvePending(message.kind === 'writeTemp:ok');
      });

      spawned.addEventListener('error', () => {
        for (const resolvePending of pending.values()) resolvePending(false);
        pending.clear();
        settle(false);
      });
    });
  }

  /** True if the worker wrote the temp file; false means "use the main-thread path". */
  async function workerWriteTemp(tempName: string, bytes: Bytes): Promise<boolean> {
    if (!(await ensureWorker()) || !worker || !workerAlive) return false;
    const id = (requestCounter += 1);
    // A copy, transferred: the caller's array must survive the postMessage intact.
    const data = bytes.slice().buffer;
    const answered = new Promise<boolean>((resolve) => pending.set(id, resolve));
    const request: WorkerRequest = { kind: 'writeTemp', id, tempName, data };
    worker.postMessage(request, [data]);
    return answered;
  }

  // ── writes ───────────────────────────────────────────────────────────────────────────────

  async function requestPersistence(): Promise<void> {
    if (persistRequested) return;
    persistRequested = true;
    try {
      await globalThis.navigator?.storage?.persist?.();
    } catch {
      // Persistence is advisory (§7.6); a refusal must never fail the write.
    }
  }

  function throwIfAborted(signal: AbortSignal | undefined, path: VfsPath): void {
    if (signal?.aborted === true) throw new OpfsError('ABORTED', path);
  }

  async function writeTempBytes(tempName: string, bytes: Bytes): Promise<void> {
    if (await workerWriteTemp(tempName, bytes)) return;
    const temps = await tempDirectory();
    const handle = await temps.getFileHandle(tempName, { create: true });
    const writable = await handle.createWritable({ keepExistingData: false });
    await writable.write(bytes);
    await writable.close();
  }

  /**
   * The no-`move()` path (D-042). `createWritable` is itself atomic — the spec writes to a swap
   * file and only swaps it in on `close()` — so the destination is never truncated. What it
   * cannot do is avoid *creating* the destination, so an abort has to undo that.
   */
  async function writeDirect(
    path: VfsPath,
    bytes: Bytes,
    signal: AbortSignal | undefined,
  ): Promise<void> {
    const existed = await exists(path);
    const handle = await fileHandle(path, true);
    const writable = await handle.createWritable({ keepExistingData: false });
    try {
      await writable.write(bytes);
      throwIfAborted(signal, path);
      await writable.close();
    } catch (error) {
      await writable.abort().catch(() => undefined);
      if (!existed) await remove(path).catch(() => undefined);
      throw wrap(error, path);
    }
  }

  async function write(
    path: string,
    data: Uint8Array | string,
    writeOptions?: WriteOptions,
  ): Promise<OpfsStat> {
    const target = requirePath(path);
    if (target === '/') throw new OpfsError('IS_A_DIRECTORY', target);
    const signal = writeOptions?.signal;
    throwIfAborted(signal, target);

    const bytes = toBytes(data);
    await requestPersistence();

    if (!renameSupported) {
      await writeDirect(target, bytes, signal);
      return requireStat(target);
    }

    const parent = await directory(parentOf(target), true);
    const tempName = `${session}-${(tempCounter += 1)}`;
    const temps = await tempDirectory();
    try {
      await writeTempBytes(tempName, bytes);
      // The commit checkpoint: bytes are durable, the destination is still untouched.
      throwIfAborted(signal, target);
      const handle = await temps.getFileHandle(tempName);
      const movable = handle as FileSystemFileHandle & MovableFileHandle;
      if (typeof movable.move !== 'function') throw new OpfsError('UNSUPPORTED', target);
      await movable.move(parent, basename(target));
    } catch (error) {
      await temps.removeEntry(tempName).catch(() => undefined);
      throw wrap(error, target);
    }
    return requireStat(target);
  }

  // ── reads ────────────────────────────────────────────────────────────────────────────────

  async function read(path: string): Promise<Uint8Array> {
    const target = requirePath(path);
    const handle = await fileHandle(target, false);
    const file = await handle.getFile();
    return new Uint8Array(await file.arrayBuffer());
  }

  async function stat(path: string): Promise<OpfsStat | null> {
    const target = requirePath(path);
    if (target === '/') return directoryStat('/');
    let parent: FileSystemDirectoryHandle;
    try {
      parent = await directory(parentOf(target), false);
    } catch {
      return null;
    }
    const name = basename(target);
    try {
      const file = await (await parent.getFileHandle(name)).getFile();
      return { path: target, name, isDir: false, size: file.size, mtime: file.lastModified };
    } catch {
      try {
        await parent.getDirectoryHandle(name);
        return directoryStat(target);
      } catch {
        return null;
      }
    }
  }

  async function requireStat(path: VfsPath): Promise<OpfsStat> {
    const info = await stat(path);
    if (!info) throw new OpfsError('NOT_FOUND', path);
    return info;
  }

  async function exists(path: string): Promise<boolean> {
    return (await stat(path)) !== null;
  }

  async function list(dir: string): Promise<OpfsStat[]> {
    const target = requirePath(dir);
    const handle = await directory(target, false);
    const out: OpfsStat[] = [];
    for await (const [name, child] of handle.entries()) {
      // The store's own scratch area is not part of the tree it presents.
      if (target === '/' && name === TEMP_DIR) continue;
      const childPath = joinVfs(target, name);
      if (child.kind === 'directory') {
        out.push(directoryStat(childPath));
        continue;
      }
      const file = await (child as FileSystemFileHandle).getFile();
      out.push({
        path: childPath,
        name,
        isDir: false,
        size: file.size,
        mtime: file.lastModified,
      });
    }
    out.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    return out;
  }

  // ── structure ────────────────────────────────────────────────────────────────────────────

  async function mkdir(path: string): Promise<OpfsStat> {
    const target = requirePath(path);
    if (target === '/') return directoryStat('/');
    await directory(target, true);
    return directoryStat(target);
  }

  async function remove(path: string, removeOptions?: { recursive?: boolean }): Promise<void> {
    const target = requirePath(path);
    if (target === '/') throw new OpfsError('REFUSED', target, 'opfs: the root cannot be removed');
    const parent = await directory(parentOf(target), false);
    try {
      await parent.removeEntry(basename(target), { recursive: removeOptions?.recursive ?? false });
    } catch (error) {
      throw wrap(error, target);
    }
    cache.invalidate(target);
  }

  async function moveFile(from: VfsPath, to: VfsPath): Promise<void> {
    const handle = await fileHandle(from, false);
    const destination = await directory(parentOf(to), true);
    const movable = handle as FileSystemFileHandle & MovableFileHandle;
    if (typeof movable.move === 'function') {
      await movable.move(destination, basename(to));
      return;
    }
    // D-042: no `move()`. Copy the bytes, then drop the original.
    const bytes = new Uint8Array(await (await handle.getFile()).arrayBuffer());
    await writeDirect(to, bytes, undefined);
    await remove(from);
  }

  async function moveDirectory(from: VfsPath, to: VfsPath): Promise<void> {
    // Chromium implements `move()` for file handles only, so a directory move is a walk.
    await mkdir(to);
    for (const entry of await list(from)) {
      const target = joinVfs(to, entry.name);
      if (entry.isDir) await moveDirectory(entry.path, target);
      else await moveFile(entry.path, target);
    }
    await remove(from, { recursive: true });
  }

  async function move(from: string, to: string): Promise<OpfsStat> {
    const source = requirePath(from);
    const destination = requirePath(to);
    if (source === destination) return requireStat(source);
    if (source === '/') throw new OpfsError('REFUSED', source, 'opfs: the root cannot be moved');
    if (isUnder(destination, source)) {
      throw new OpfsError('REFUSED', destination, 'opfs: a directory cannot be moved into itself');
    }

    const info = await stat(source);
    if (!info) throw new OpfsError('NOT_FOUND', source);
    if (info.isDir) await moveDirectory(source, destination);
    else await moveFile(source, destination);

    cache.invalidate(source);
    return requireStat(destination);
  }

  // ── diagnostics ──────────────────────────────────────────────────────────────────────────

  function invalidateCached(path: string): void {
    const parsed = normalizeVfsPath(path);
    if (parsed.ok) cache.invalidate(parsed.path);
  }

  async function estimate(): Promise<{ usage: number; quota: number }> {
    const storage: StorageManager | undefined = globalThis.navigator?.storage;
    if (typeof storage?.estimate !== 'function') return { usage: 0, quota: 0 };
    const report = await storage.estimate();
    return { usage: report.usage ?? 0, quota: report.quota ?? 0 };
  }

  async function diagnostics(): Promise<OpfsDiagnostics> {
    let tempEntries = 0;
    try {
      const temps = await tempDirectory();
      for await (const _entry of temps.entries()) tempEntries += 1;
    } catch {
      tempEntries = 0;
    }
    const mode: WriteMode =
      workerReady === null ? 'unknown' : workerAlive ? 'sync-worker' : 'writable';
    return { writeMode: mode, renameSupported, cachedHandles: cache.size, tempEntries };
  }

  function dispose(): void {
    worker?.terminate();
    worker = null;
    workerAlive = false;
    workerReady = null;
    pending.clear();
    cache.clear();
    rootPromise = null;
  }

  return {
    read,
    write,
    list,
    mkdir,
    move,
    remove,
    stat,
    exists,
    estimate,
    diagnostics,
    invalidate: invalidateCached,
    dispose,
  };
}
